import { Injectable } from '@angular/core'
import { EMPTY, Observable, of } from 'rxjs'
import { catchError, map, switchMap } from 'rxjs/operators'
import * as R from 'ramda'
import * as moment from 'moment'
import { HttpClient } from '@angular/common/http'
import { BookingEngineCostResponse } from '@tv3/interfaces/responses/booking-engine-cost.response'
import { BookingEngineCostRequest } from '@tv3/interfaces/requests/booking-engine-cost.request'
import { BookingEngineCost } from '@tv3/models/inquiry/booking-engine-cost'
import { AddInquiryForm } from '@tv3/interfaces/forms/add-inquiry.form'
import { Inquiry, InquiryPaymentStatus } from '@tv3/store/inquiry/inquiry.model'
import { UpdateQuoteDetailsForm } from '@tv3/interfaces/forms/update-quote-details.form'
import { convertBookingEngine2Cost, InquiryCost, parseBookingCharges } from '@tokeet/cost-resolver'
import { Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import {
  asUTCEpoch,
  HttpGetRequestCacheService,
  isSomething,
  selectChannelByName,
  loadRentalsComplete,
  LocalStorage,
  RentalService,
  selectAllRentals,
  selectOnce,
  TOKEET_CHANNEL_ID,
  TOKEET_CHANNEL_RATE_CATEGORY,
  tokeetDashboardChannel,
} from '@tokeet-frontend/tv3-platform'
import { ActionFailed } from '@tokeet-frontend/tv3-platform'
import { selectChannelWithCustomByName } from '@tv3/store/channel/selectors'

@Injectable({
  providedIn: 'root',
})
export class InquiryChargeService {
  constructor(
    private http: HttpClient,
    private store: Store<fromRoot.State>,
    private httpGetRequestCacheService: HttpGetRequestCacheService
  ) {}

  canCallEngineCost(form: AddInquiryForm): boolean {
    return !!form.rentalId && !!form.adults && !!form.arriveDate && !!form.departDate
  }

  convertFormToBookingEngineRequest(
    form: AddInquiryForm,
    isChargeUserDefined = false,
    ignore_fees: string[] = []
  ): Observable<BookingEngineCostRequest> {
    const start = asUTCEpoch(form.arriveDate, 'start')
    const end = moment(form.departDate).isValid()
      ? asUTCEpoch(form.departDate, 'start')
      : moment
          .utc(start * 1000)
          .add(1, 'day')
          .startOf('day')
          .unix()

    const request = {
      start: start,
      end: end,
      adults: form.adults,
      child: form.children || 0,
      category: TOKEET_CHANNEL_RATE_CATEGORY,
      rid: form.rentalId,
      discounts: R.filter((d: { amount: number }) => !R.isEmpty(d.amount), form.discounts),
      ignore_fees,
    } as BookingEngineCostRequest

    if (isChargeUserDefined) {
      request.usercharge = form.charge
    }

    if (form.source) {
      return this.store.pipe(
        selectOnce(selectChannelWithCustomByName(form.source)),
        map((channel) => {
          request.channel_id = (channel && channel.channelId) || TOKEET_CHANNEL_ID
          return request
        })
      )
    } else {
      request.channel_id = TOKEET_CHANNEL_ID
      return of(request)
    }
  }

  // set ignoreUserCharge to true to recalculate charge
  convertInquiryToBookingEngineRequest(
    inquiry: Inquiry,
    form?: UpdateQuoteDetailsForm,
    ignoreUserCharge = false,
    ignore_fees: string[] = []
  ): Observable<BookingEngineCostRequest> {
    const request = {
      start: inquiry.guestArrive,
      end: inquiry.guestDepart,
      adults: inquiry.numAdults,
      child: inquiry.numChild,
      category: form?.rateCategory || 'default',
      rid: inquiry.rentalId,
      discounts: R.filter((d: { amount: number }) => !R.isEmpty(d.amount), form?.discounts || []),
      ignore_fees,
    } as BookingEngineCostRequest

    if (!ignoreUserCharge) {
      request.usercharge = form?.charge || 0
    }

    if (isSomething(inquiry.inquirySource)) {
      return this.store.pipe(
        selectOnce(selectChannelWithCustomByName(inquiry.inquirySource)),
        map((channel) => {
          request.channel_id = (channel && channel.channelId) || TOKEET_CHANNEL_ID
          return request
        })
      )
    } else {
      request.channel_id = TOKEET_CHANNEL_ID
      return of(request)
    }
  }

  /**
   * request can contain {userCharge: number} to make booking engine calculate with user defined charge
   */
  getEngineCost(request: BookingEngineCostRequest): Observable<BookingEngineCost | null> {
    const url = `@api/bookingengine/cost/${request.rid}`

    request.channel_id = request.channel_id || tokeetDashboardChannel.id

    if (request.adults === 0) {
      request.adults = 1
    }
    if (!request.category || request.category === 'default') {
      request.category = TOKEET_CHANNEL_RATE_CATEGORY
    }

    // Several components request the same cost concurrently (list rows, sidebar, overlay) —
    // identical url+body requests are coalesced and served from a short-lived cache
    return this.httpGetRequestCacheService
      .cachePostRequest<BookingEngineCostResponse>(url, request, { ttl: 30 * 1000 })
      .pipe(
        map((response) => {
          if (R.isEmpty(response.cost)) {
            return null
          } else {
            return BookingEngineCost.deserialize(response.cost)
          }
        }),
        catchError(() => EMPTY)
      )
  }

  getCharges(inquiry: Inquiry): Observable<InquiryCost | null> {
    if (inquiry.cost || inquiry.bookingEngine || inquiry.quotes) {
      return of(parseBookingCharges(inquiry))
    }

    return this.convertInquiryToBookingEngineRequest(inquiry).pipe(
      switchMap((payload) => this.getEngineCost(payload)),
      map((cost) => (cost ? convertBookingEngine2Cost(cost) : null))
    )
  }

  getBookingPaymentStatus(inquiryId: string, amount: number, refresh = false) {
    const url = `@api/inquiry/paid/${inquiryId}?amt=${amount}`
    return this.httpGetRequestCacheService.cacheGetRequest<{ paid: number; remaining: number }>(url, {
      ttl: 30 * 1000,
      refresh,
    })
  }

  getBookingPaymentStatusForInquiry(
    inquiry: Inquiry,
    refresh = false
  ): Observable<{ paid: boolean; remaining: number; status: InquiryPaymentStatus }> {
    return this.getCharges(inquiry).pipe(
      switchMap((charges) => {
        if (!charges || charges.sum == 0) {
          return of({
            paid: true,
            remaining: 0,
            status: InquiryPaymentStatus.Paid,
          })
        }

        const sum = +(+charges.sum).toFixed(2)
        return this.getBookingPaymentStatus(inquiry.id, sum, refresh).pipe(
          map(({ paid, remaining }) => ({
            paid: !!paid,
            remaining: remaining < 0 ? 0 : remaining,
            status: !!paid
              ? InquiryPaymentStatus.Paid
              : remaining < sum
              ? InquiryPaymentStatus.PartialPaid
              : InquiryPaymentStatus.Unpaid,
          })),
          catchError((error) => {
            this.store.dispatch(ActionFailed({ error }))
            return EMPTY
          })
        )
      })
    )
  }
}
